import React from 'react';
import { Paper, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IC, IFontSizes, IFontWeights, IRadius, INumericSx, ILineHeight } from '../insightsTheme';

const fmtEur = (v) =>
  new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(Number(v) || 0);

/**
 * YoyBySector — real YoY comparison.
 *
 * Data shape (from /sales-patterns/yoy-comparison, camelCase):
 *   { overall: { currentProfit, lastYearProfit, percentChange, dayOfYear },
 *     sectors: [{ sector, currentProfit, lastYearProfit, absoluteChange, percentChange, percentContribution }] }
 */
const YoyBySector = ({ data, loading = false }) => {
  const { t } = useTranslation();
  const overall = data?.overall;
  const sectors = Array.isArray(data?.sectors) ? data.sectors : [];

  // Normalize contributions: absolute contribution percentage relative to max |percentContribution|
  const maxAbs = Math.max(1, ...sectors.map((s) => Math.abs(Number(s.percentContribution) || 0)));

  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: `${IRadius.card}px`,
        borderColor: IC.bdr,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${IC.bdr}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: `${IFontSizes.md}px`, fontWeight: IFontWeights.strong, color: IC.tx }}>
            {t('insights_v2_yoy_title')}
          </Typography>
          <Typography sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.txM }}>
            {t('insights_v2_yoy_subtitle')}
          </Typography>
        </Box>
        {overall && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.txM }}>
              {t('insights_v2_yoy_overall')}
            </Typography>
            <Typography
              sx={{
                fontSize: `${IFontSizes.md}px`,
                fontWeight: IFontWeights.strong,
                color: Number(overall.percentChange) >= 0 ? IC.grn : IC.red,
                ...INumericSx,
              }}
            >
              {Number(overall.percentChange) >= 0 ? '+' : ''}
              {Math.round(Number(overall.percentChange) || 0)}%
            </Typography>
          </Box>
        )}
      </Box>

      {loading && sectors.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: `${IFontSizes.sm}px`, color: IC.txM }}>
            {t('insights_v2_common_loading')}
          </Typography>
        </Box>
      ) : sectors.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: `${IFontSizes.sm}px`, color: IC.txM }}>
            {t('insights_v2_yoy_empty')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ p: 1.5 }}>
          {sectors.slice(0, 6).map((s, i) => {
            const pctContrib = Number(s.percentContribution) || 0;
            const abs = Number(s.absoluteChange) || 0;
            const isUp = abs >= 0;
            const color = isUp ? IC.grn : IC.red;
            const barPct = Math.max(4, Math.round((Math.abs(pctContrib) / maxAbs) * 100));
            return (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                <Typography
                  sx={{
                    width: 110,
                    fontSize: `${IFontSizes.sm}px`,
                    color: IC.tx2,
                    fontWeight: IFontWeights.label,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: ILineHeight.feed,
                  }}
                  title={s.sector || t('insights_v2_yoy_unclassified')}
                >
                  {s.sector || t('insights_v2_yoy_unclassified')}
                </Typography>
                <Box sx={{ flex: 1, height: 6, borderRadius: 3, background: IC.bdrL, overflow: 'hidden' }}>
                  <Box sx={{ width: `${barPct}%`, height: '100%', borderRadius: 3, background: color }} />
                </Box>
                <Typography
                  sx={{
                    width: 72,
                    textAlign: 'right',
                    fontSize: `${IFontSizes.sm}px`,
                    fontWeight: IFontWeights.strong,
                    color,
                    ...INumericSx,
                  }}
                >
                  {isUp ? '+' : '-'}
                  {fmtEur(Math.abs(abs))}€
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default YoyBySector;
